import React, { useEffect, useRef } from 'react';
import Navbar from '../../pages/home/components/Navbar';
import Footer from '../../pages/home/components/Footer';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

export const PageLayout: React.FC<{
  title: React.ReactNode;
  subtitle: string;
  badge?: string;
  heroLogoSrc?: string;
  imageSrc?: string;
  children: React.ReactNode;
}> = ({ title, subtitle, badge, heroLogoSrc, imageSrc, children }) => {
  const headerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Scroll to top on mount
    window.scrollTo(0, 0);
    
    let ctx = gsap.context(() => {
      gsap.fromTo(headerRef.current,
        { opacity: 0, y: 40 },
        { opacity: 1, y: 0, duration: 1, ease: 'power3.out' }
      );
      
      gsap.fromTo(contentRef.current,
        { opacity: 0, y: 40 },
        { opacity: 1, y: 0, duration: 1, delay: 0.2, ease: 'power3.out' }
      );
    });
    return () => ctx.revert();
  }, [title]); // re-run animation if route changes but component stays mounted

  return (
    <div className="min-h-screen bg-[#fafafa] font-sans selection:bg-blue-100 overflow-x-hidden">
      <Navbar />
      
      <main id="main-content" className="pt-32 pb-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div ref={headerRef} className="text-center max-w-4xl mx-auto mb-20 opacity-0">
            {badge && (
              <span className="inline-block px-4 py-1.5 rounded-full bg-blue-50 text-blue-700 font-bold text-sm mb-6 border border-blue-100">
                {badge}
              </span>
            )}
            {heroLogoSrc && (
              <div className="flex justify-center -mb-8 md:-mb-16">
                <img src={heroLogoSrc} alt="Logo" className="h-56 md:h-80 w-auto object-contain mix-blend-multiply" />
              </div>
            )}
            <h1 className="text-5xl md:text-6xl font-extrabold text-gray-900 mb-6 tracking-tight leading-tight">
              {title}
            </h1>
            <p className="text-xl text-gray-500 font-medium">
              {subtitle}
            </p>
            
            {imageSrc && (
              <div className="mt-12 rounded-3xl overflow-hidden shadow-2xl border border-gray-100 relative max-h-[400px]">
                <img src={imageSrc} alt={typeof title === 'string' ? title : 'Page hero image'} className="w-full h-full object-cover" />
              </div>
            )}
          </div>

          <div ref={contentRef} className="opacity-0">
            {children}
            
            {/* Standardized CTA */}
            <div className="mt-24 text-center bg-blue-50 rounded-[2.5rem] p-16 border border-blue-100 shadow-inner">
              <h3 className="text-3xl font-extrabold text-gray-900 mb-4">Ready to upgrade your infrastructure?</h3>
              <p className="text-blue-800 mb-8 max-w-2xl mx-auto">Get access to wholesale rates and powerful developer tools instantly.</p>
              <button 
                onClick={() => window.location.href = 'https://paygent.transactiongateway.com'}
                className="px-8 py-4 bg-blue-600 text-white font-bold rounded-xl hover:bg-blue-700 hover:shadow-lg transition-all transform hover:-translate-y-0.5"
              >
                Start Building Now
              </button>
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};
